"use strict";
/**
 * Video-voting results: the totals each prompt shows, counted from the votes
 * cast on it, plus the math for applying the viewer's own vote change to them.
 *
 * Lives in `@repo/shared` (not the server) so the server's results read, the
 * client's optimistic vote patch, and client demo fixtures all count the same
 * way. The voter is a type parameter because the server tallies voter keys and
 * resolves profiles only for the few it sends, while fixtures hold profiles.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.MAX_NAMED_VOTERS = void 0;
exports.tallyVideoVotes = tallyVideoVotes;
exports.applyMyVideoVote = applyMyVideoVote;
/** How many voters each option names, as many as its face pile shows. */
exports.MAX_NAMED_VOTERS = 3;
/**
 * Totals for each prompt, in the voting's order. Each option names its most
 * recent non-anonymous voters, newest first.
 */
function tallyVideoVotes({ questions, votes }) {
    return questions.map((question) => {
        const onPrompt = votes.filter((vote) => vote.questionKey === question.key);
        const newestFirst = [...onPrompt].sort((a, b) => b.time - a.time);
        const mineVote = onPrompt.find((vote) => vote.mine);
        const isSlider = question.type === 'slider';
        return {
            questionKey: question.key,
            total: onPrompt.length,
            options: isSlider
                ? []
                : (question.options ?? []).map((option) => {
                    const forOption = newestFirst.filter((vote) => vote.optionKey === option.key);
                    return {
                        optionKey: option.key,
                        count: forOption.length,
                        voters: forOption
                            .flatMap((vote) => (vote.anonymous || vote.voter == null ? [] : [vote.voter]))
                            .slice(0, exports.MAX_NAMED_VOTERS),
                    };
                }),
            sum: isSlider ? onPrompt.reduce((sum, vote) => sum + (vote.value ?? 0), 0) : null,
            mine: mineVote ? { optionKey: mineVote.optionKey, value: mineVote.value, anonymous: mineVote.anonymous } : null,
        };
    });
}
/**
 * Applies the viewer's vote change to one prompt's totals: their previous
 * answer, if any, stops counting, and `next`, if any, starts. `me` is the face
 * shown for a named vote; it is matched by `isMe` to take the old one away.
 * Faces of other voters beyond the shown few are unknown here, so taking the
 * viewer's face away can leave one short until the next read.
 */
function applyMyVideoVote(pre, { next, me, isMe }) {
    const previous = pre.mine;
    let total = pre.total;
    let sum = pre.sum;
    let options = pre.options.map((option) => ({ ...option, voters: option.voters.filter((voter) => !isMe(voter)) }));
    if (previous) {
        total -= 1;
        if (sum != null)
            sum -= previous.value ?? 0;
        options = options.map((option) => (option.optionKey === previous.optionKey ? { ...option, count: option.count - 1 } : option));
    }
    if (next) {
        total += 1;
        if (sum != null)
            sum += next.value ?? 0;
        options = options.map((option) => option.optionKey === next.optionKey
            ? {
                ...option,
                count: option.count + 1,
                voters: next.anonymous || me == null ? option.voters : [me, ...option.voters].slice(0, exports.MAX_NAMED_VOTERS),
            }
            : option);
    }
    return { ...pre, total, sum, options, mine: next };
}
//# sourceMappingURL=video-voting-results.js.map